import React, { useState, useRef } from 'react';
import { Box, Sheet, Stack, Typography, IconButton, Button } from '@mui/joy';
import { format } from 'date-fns';
import { categoryEmoji, accountDisplayName } from '../categoryMeta';

const REVEAL = 88; // ширина «выезжающей» кнопки удаления

// Карточка одной операции. Свайп влево открывает кнопку «Удалить» (жест намеренный,
// поэтому подтверждение не нужно). Карандаш — редактирование.
function SwipeableExpenseCard({ expense, dateFormat = 'HH:mm', onEdit, onDelete, deleting }) {
    const [dx, setDx] = useState(0);
    const [dragging, setDragging] = useState(false);
    const revealed = useRef(false);
    const startX = useRef(null);
    const startDx = useRef(0);

    // Pointer Events вместо Touch — один код для тача и для мыши (свайп мышью на компьютере).
    const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Кнопки внутри карточки должны получать собственные pointerup/click.
        // Захват указателя карточкой перенаправляет клик с карандаша на Sheet.
        if (e.target.closest('button, a, input, select, textarea, [role="button"]')) return;
        startX.current = e.clientX;
        startDx.current = revealed.current ? -REVEAL : 0;
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (startX.current == null) return;
        let next = startDx.current + (e.clientX - startX.current);
        if (next > 0) next = 0;
        if (next < -REVEAL) next = -REVEAL;
        setDx(next);
    };
    const onPointerEnd = (e) => {
        if (startX.current == null) return;
        const shouldReveal = dx < -REVEAL / 2;
        revealed.current = shouldReveal;
        setDx(shouldReveal ? -REVEAL : 0);
        setDragging(false);
        startX.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
    };

    const cat = expense.category || 'Без категории';
    const isIncome = cat === 'ПОПОЛНЕНИЕ';
    const isAdjust = cat === 'КОРРЕКТИРОВКА';
    const amt = Number(expense.amount);
    const amountColor = isIncome ? 'success.plainColor' : isAdjust ? 'neutral.plainColor' : 'text.primary';
    const amountText = isIncome
        ? `+${Math.abs(amt).toLocaleString('ru-RU')} ₽`
        : isAdjust
            ? `${amt >= 0 ? '+' : '−'}${Math.abs(amt).toLocaleString('ru-RU')} ₽`
            : `${amt.toLocaleString('ru-RU')} ₽`;

    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 'sm', mb: 1 }}>
            {/* Задний слой: кнопка удаления */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    color="danger"
                    variant="solid"
                    loading={deleting}
                    onClick={() => onDelete(expense)}
                    sx={{ width: REVEAL, borderRadius: 0 }}
                    aria-label={`Удалить: ${cat.toLowerCase()}`}
                >
                    Удалить
                </Button>
            </Box>

            {/* Передний слой: карточка */}
            <Sheet
                variant="outlined"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                sx={{
                    position: 'relative',
                    transform: `translateX(${dx}px)`,
                    transition: dragging ? 'none' : 'transform 0.2s ease',
                    borderRadius: 'sm',
                    p: 1.5,
                    bgcolor: 'background.surface',
                    touchAction: 'pan-y',
                    cursor: 'grab'
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">
                        {categoryEmoji(cat)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" noWrap>{cat.toLowerCase()}</Typography>
                        {expense.description ? (
                            <Typography level="body-xs" color="neutral" noWrap>
                                {expense.description}
                            </Typography>
                        ) : null}
                        {expense.account_name && !expense.account_is_default ? (
                            <Typography level="body-xs" color="neutral" noWrap>
                                💳 {accountDisplayName(expense.account_name)}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography level="title-sm" sx={{ color: amountColor }}>{amountText}</Typography>
                        <Typography level="body-xs" color="neutral">
                            {expense.date ? format(expense.date, dateFormat) : ''}
                        </Typography>
                    </Box>
                    {!isAdjust && onEdit && (
                        <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => onEdit(expense)}
                            aria-label={`Редактировать: ${cat.toLowerCase()}`}
                        >
                            ✎
                        </IconButton>
                    )}
                </Stack>
            </Sheet>
        </Box>
    );
}

export default SwipeableExpenseCard;
